import { useState, type FormEvent } from "react";
import { KeyRound, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { changePassword } from "@/lib/api/auth";
import { useDialog } from "@/lib/dialogs/DialogProvider";
import { useAsyncAction } from "@/lib/hooks/useAsyncAction";

export function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const { alert } = useDialog();
  const { run, pending } = useAsyncAction();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run(async () => {
      if (password !== confirmation) throw new Error("Die Passwörter stimmen nicht überein.");
      await changePassword(password);
      await alert("Passwort erfolgreich geändert.", { title: "Passwort geändert" });
      setPassword("");
      setConfirmation("");
      onOpenChange(false);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="size-4" aria-hidden="true" /> Passwort ändern
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="avatar-menu-new-pass">Neues Passwort</FieldLabel>
              <Input
                id="avatar-menu-new-pass"
                type="password"
                placeholder="Mindestens 12 Zeichen"
                required
                minLength={12}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="avatar-menu-new-pass-confirm">Neues Passwort bestätigen</FieldLabel>
              <Input
                id="avatar-menu-new-pass-confirm"
                type="password"
                placeholder="Passwort wiederholen"
                required
                minLength={12}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </Field>
          </FieldGroup>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={pending} className="hover:bg-primary-dark">
              Passwort speichern <Save className="size-4" aria-hidden="true" />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
